import { useEffect } from 'react';
import { Alert, Button, Select, Space, Table, Tag } from 'antd';
import type { WizardState, WizardAction, ColumnTarget } from '../wizardReducer';
import {
	suggestColumnMappings,
	sampleValuesForColumn,
	TARGET_LABELS,
	TARGET_COLORS,
} from '../directAllocationMappingHelpers';
import { isTemplateMode, mappingProblem } from '../directAllocationResolve';
import styles from './Step2MapColumns.module.scss';

interface Step2MapColumnsProps {
	state: WizardState;
	dispatch: React.Dispatch<WizardAction>;
	onCancel: () => void;
}

const TARGET_OPTIONS: ColumnTarget[] = ['employee', 'percent', 'customer', 'class', 'skip'];

/**
 * Step 2 — Map Columns. A single fixed-target mapping table (File Column Name |
 * Sample Data | Category | Map To Field) — no dynamic section/field tree, no
 * pending-field creation, no mapping templates (out of scope for v1). Category
 * and Map To Field both reflect one underlying per-column target, chosen via the
 * Map To Field select; Category renders it as a colored Tag.
 */
export default function Step2MapColumns({ state, dispatch, onCancel }: Step2MapColumnsProps) {
	const headers = (state.rawMatrix[state.headerRowIndex] ?? []).map((h) => String(h));

	useEffect(() => {
		if (state.columnMappings.length > 0) return;
		const sampleValuesByHeader: Record<string, string[]> = {};
		headers.forEach((h) => {
			sampleValuesByHeader[h] = sampleValuesForColumn(state.rawRows, h);
		});
		dispatch({
			type: 'COLUMN_MAPPINGS_INITIALIZED',
			columnMappings: suggestColumnMappings(headers, sampleValuesByHeader),
		});
		// Only runs once per parsed file — guarded in the reducer against re-entry.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const problem = mappingProblem(state.columnMappings);
	const canContinue = state.columnMappings.length > 0 && problem === null;
	const templateMode = isTemplateMode(state.columnMappings);
	const hasPercent = state.columnMappings.some((m) => m.target === 'percent');

	const columns = [
		{
			title: 'File Column Name',
			dataIndex: 'sourceHeader',
			key: 'sourceHeader',
		},
		{
			title: 'Sample Data',
			dataIndex: 'sampleValues',
			key: 'sampleValues',
			render: (values: string[]) =>
				values.length > 0 ? (
					<span className={styles['step2__sample']}>{values.join(', ')}</span>
				) : (
					<span className={styles['step2__muted']}>—</span>
				),
		},
		{
			title: 'Category',
			dataIndex: 'target',
			key: 'category',
			render: (target: ColumnTarget) => (
				<Tag color={TARGET_COLORS[target]}>{TARGET_LABELS[target]}</Tag>
			),
		},
		{
			title: 'Map To Field',
			dataIndex: 'target',
			key: 'mapToField',
			render: (target: ColumnTarget, record: WizardState['columnMappings'][number]) => (
				<Select
					className={styles['step2__select']}
					value={target}
					onChange={(value: ColumnTarget) =>
						dispatch({
							type: 'COLUMN_TARGET_CHANGED',
							colIndex: record.colIndex,
							target: value,
						})
					}
					options={TARGET_OPTIONS.map((t) => ({ value: t, label: TARGET_LABELS[t] }))}
				/>
			),
		},
	];

	return (
		<div className={styles['step2']}>
			<Alert
				className={styles['step2__banner']}
				type="info"
				showIcon
				message={`${headers.length} column(s) detected, ${state.rawRows.length} data row(s).`}
			/>

			<Table
				className={styles['step2__table']}
				dataSource={state.columnMappings}
				columns={columns}
				rowKey="colIndex"
				pagination={false}
				scroll={{ y: 400 }}
				size="small"
			/>

			{canContinue && templateMode && (
				<Alert
					className={styles['step2__banner']}
					type="warning"
					showIcon
					message="Your file has no Employee column."
					description={`On the next step, choose the employees this allocation applies to.${
						hasPercent ? '' : ' Your file also has no % of Hours column, so you\'ll enter the percentages (or split them equally) there too.'
					}`}
				/>
			)}
			{canContinue && !templateMode && !hasPercent && (
				<Alert
					className={styles['step2__banner']}
					type="warning"
					showIcon
					message="Your file has no % of Hours column."
					description="On the next step, enter each employee's percentages or split them equally."
				/>
			)}

			{problem && <p className={styles['step2__validation']}>{problem}</p>}

			<div className={styles['step2__footer']}>
				<Space>
					<Button className={styles['step2__btn-secondary']} onClick={onCancel}>
						Cancel
					</Button>
					<Button
						className={styles['step2__btn-secondary']}
						onClick={() => dispatch({ type: 'BACK_STEP' })}
					>
						Previous
					</Button>
					<Button
						type="primary"
						disabled={!canContinue}
						onClick={() => dispatch({ type: 'ADVANCE_STEP' })}
						className="btn-black"
						style={{ height: '41px' }}
					>
						Continue
					</Button>
				</Space>
			</div>
		</div>
	);
}
